import { useRef, useState, useContext, useCallback, useMemo, useEffect } from 'react'
import * as React from 'react'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LinePlaneIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import { FormattedMessage, useIntl } from 'react-intl'
import { sum } from 'lib/array/arrayUtils'
import Modal from 'LuxKit/Modal/Modal'
import { FlightsClassTypes, isRewardFareClass, maxFlightChildAge, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import ModalContext from 'contexts/ModalContext'
import MaldivesCharterFlightFareClassModal from 'components/OfferPage/Filters/CharterFlight/MaldivesCharterFlightFareClassModal'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import OfferAirportOptionsGroupedList from 'components/OfferPage/Filters/Airport/OfferAirportOptionsGroupedList'
import { MELBOURNE_CHARTER_EXCLUDED_AIRPORT_CODES } from 'constants/charterFlight'
import useCharterFlightLabels, { getCharterFareClassDisplayLabel } from 'hooks/Flights/useCharterFlightLabels'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import { validateSemiForm } from 'lib/forms/formUtils'
import config from 'constants/config'
import FormattedOccupancy from 'components/Common/OccupancyPicker/FormattedOccupancy'
import MaldivesOccupancySelect from './MaldivesOccupancySelect'
import Backdrop from 'components/utils/Backdrop'
import { useAppSelector } from 'hooks/reduxHooks'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import { smartScrollIntoViewIfNeeded } from 'lib/window/scrollUtils'
import usePopularAirports from 'hooks/Flights/usePopularAirports'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import NoAirportsOptionListItem from 'components/Flights/FlightsSearch/SearchWidget/FlightAirportSelect/NoAirportsOptionListItem'
import CharterDurationToggleControl from 'components/OfferPage/Filters/CharterFlight/CharterDurationToggleControl'
import { ALL_CHARTER_FARE_CLASSES, getDefaultFareClassFromAllowed } from 'lib/charterFlights/charterOfferFareClassRestrictions'

const BundleSearchSection = styled.section`
  padding-top: ${rem(40)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${rem(12)};
  
  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
    gap: ${rem(12)};
  }
  
  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
    gap: ${rem(12)};
  }
`

const FilterListItem = styled(ClickableListItem)<{ $mobileFullWidth?: boolean }>`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  
  /* Increase specificity to override default padding */
  && {
    padding-block: ${rem(10)};
    padding-inline: ${rem(16)};
  }
  
  /* Override the uppercase transformation from the overline */
  & * {
    text-transform: none !important;
  }
  
  &:hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  ${props => props.$mobileFullWidth && css`
    grid-column: 1 / -1;
  `}

  ${mediaQueryUp.tablet} {
    grid-column: auto;
  }
`

// Props interface
type BundleType = '5-nights' | '12-nights'

interface MaldivesSearchState {
  rooms: Array<App.Occupants>
  selectedBundle: BundleType
  departureAirport: App.Airport
  flightClass?: FareClassWithReward
  luxPlusEnabled: boolean
  checkInDate?: string
  checkOutDate?: string
  departDate?: string
  returnDate?: string
}

interface Props {
  searchState: MaldivesSearchState
  onRoomsChange: (rooms: Array<App.Occupants>) => void
  onRoomsApply: (rooms: Array<App.Occupants>) => void
  onBundleChange: (bundle: BundleType) => void
  onAirportChange: (airport: App.Airport) => void
  onFlightClassChange: (flightClass: FareClassWithReward) => void
  onDateSelect: (departDate: string, returnDate: string) => void
  onOpenFlightCalendar: () => void
  availableFareClasses: Array<FlightsClassTypes>
  rewardSeatsOnly?: boolean
  onRewardSeatsOnlyChange?: (enabled: boolean) => void
  onCabinFilterChange?: (cabin: FlightsClassTypes | null) => void
}

type OpenFilter = 'travellers' | 'airport' | undefined

const hiddenMelbourneAirportCodes = new Set<string>(MELBOURNE_CHARTER_EXCLUDED_AIRPORT_CODES) // Charter departs from MEL only

function MaldivesDirectSearchFilters(props: Props) {
  const {
    searchState,
    onRoomsChange,
    onRoomsApply,
    onBundleChange,
    onAirportChange,
    onFlightClassChange,
    onOpenFlightCalendar,
    availableFareClasses,
    rewardSeatsOnly,
    onRewardSeatsOnlyChange,
    onCabinFilterChange,
  } = props
  const { charterBundleDurationOptions } = useCharterFlightLabels()
  const [openFilter, setOpenFilter] = useState<OpenFilter>(undefined)

  const filtersGridRef = useRef<HTMLDivElement>(null)
  const filtersBlockRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLButtonElement>(null)
  const airportButtonRef = useRef<HTMLButtonElement>(null)
  const datesButtonRef = useRef<HTMLButtonElement>(null)
  const travellersDropdownRef = useRef<HTMLDivElement>(null)
  const travellersMobileRef = useRef<HTMLDivElement>(null)
  const airportDropdownRef = useRef<HTMLDivElement>(null)
  const isMobile = useScreenSizeOnly('mobile')
  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')
  const [airportSearchPhrase, setAirportSearchPhrase] = useState('')

  const openModal = useContext(ModalContext)

  const australianAirports = useAppSelector(state => state.geo.originAirports)
  const popularDepartureAirports = usePopularAirports('departure')
  const airportRanking = useMemo(() => {
    const ranking = new Map<string, number>()
    popularDepartureAirports.forEach((airport, index) => {
      ranking.set(airport.airportCode, index)
    })
    return ranking
  }, [popularDepartureAirports])
  const durationToggleOptions = useMemo(() => charterBundleDurationOptions.map(option => ({
    label: option.label,
    value: option.value,
  })), [charterBundleDurationOptions])
  const airports = useMemo(() => {
    return australianAirports
      .filter(airport => airport.countryCode === 'AU' && !hiddenMelbourneAirportCodes.has(airport.code))
      .slice()
      .sort((a, b) => {
        const rankA = airportRanking.get(a.code)
        const rankB = airportRanking.get(b.code)

        if (rankA !== undefined && rankB !== undefined) {
          return rankA - rankB
        }

        if (rankA !== undefined) {
          return -1
        }

        if (rankB !== undefined) {
          return 1
        }

        return a.name.localeCompare(b.name)
      })
  }, [australianAirports, airportRanking])
  const filteredAirports = useMemo(() => {
    const normalizedValue = airportSearchPhrase.trim().toLowerCase()
    if (!normalizedValue) {
      return airports
    }
    return airports.filter(airport => `${airport.name} ${airport.code}`.toLowerCase().includes(normalizedValue))
  }, [airports, airportSearchPhrase])
  const showNoAirportResults = !!airportSearchPhrase && filteredAirports.length === 0

  const handleDurationToggleChange = useCallback((selectedNights: number) => {
    if (selectedNights === 12) {
      onBundleChange('12-nights')
      return
    }
    onBundleChange('5-nights')
  }, [onBundleChange])

  // Scroll dropdown into view when it opens
  useEffect(() => {
    if (!openFilter || !isTabletScreenSizeAndUp) {
      return
    }

    // Wait for Floating UI to position the dropdown
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const elementToScroll =
          openFilter === 'travellers' ? travellersDropdownRef.current :
            openFilter === 'airport' ? airportDropdownRef.current :
              null

        if (elementToScroll) {
          smartScrollIntoViewIfNeeded(elementToScroll)
        }
      })
    })
  }, [openFilter, isTabletScreenSizeAndUp])

  // Local state for in-progress room edits. Only flushed to URL on Apply/Close
  // so transient placeholder ages (-1) never leak into the URL or trigger search requests.
  const [pendingRooms, setPendingRooms] = useState<Array<App.Occupants>>(searchState.rooms)
  const isTravellersOpen = openFilter === 'travellers'

  useEffect(() => {
    if (!isTravellersOpen) {
      setPendingRooms(searchState.rooms)
    }
  }, [isTravellersOpen, searchState.rooms])

  function handleTravellersToggle() {
    // Scroll filter button into view when clicked
    if (isTabletScreenSizeAndUp && filtersBlockRef.current) {
      smartScrollIntoViewIfNeeded(filtersBlockRef.current)
    }
    setOpenFilter(prev => prev === 'travellers' ? undefined : 'travellers')
  }

  function closeTravellersMenu() {
    onRoomsChange(pendingRooms)
    setOpenFilter(undefined)
  }

  function dismissTravellersMenu() {
    setPendingRooms(searchState.rooms)
    setOpenFilter(undefined)
  }

  function handleRoomChange(newRooms: Array<App.Occupants>) {
    setPendingRooms(newRooms)
  }

  function handleRoomsApply() {
    if (isMobile && travellersMobileRef.current && !validateSemiForm(travellersMobileRef.current)) {
      return
    }
    onRoomsChange(pendingRooms)
    onRoomsApply(pendingRooms)
    setOpenFilter(undefined)
  }

  const handleAirportSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setAirportSearchPhrase(event.target.value)
  }, [])

  function handleAirportToggle() {
    // Scroll filter button into view when clicked
    if (isTabletScreenSizeAndUp && filtersBlockRef.current) {
      smartScrollIntoViewIfNeeded(filtersBlockRef.current)
    }
    setOpenFilter(prev => prev === 'airport' ? undefined : 'airport')
  }

  function closeAirportDropdown() {
    setOpenFilter(undefined)
    setAirportSearchPhrase('')
  }

  const handleAirportSelect = useCallback((airport: App.AirportLocation) => {
    onAirportChange({
      code: airport.airportCode,
      name: airport.airportName,
      latitude: airport.lt!,
      longitude: airport.lg!,
      countryCode: airport.country,
    })
    closeAirportDropdown()
  }, [onAirportChange])

  const getAirportOptionsList = useCallback((withRadioButtons?: boolean) => {
    if (showNoAirportResults) {
      return <NoAirportsOptionListItem inputValue={airportSearchPhrase} />
    }
    return (
      <OfferAirportOptionsGroupedList
        airports={filteredAirports}
        handleItemClick={handleAirportSelect}
        selectedAirport={searchState.departureAirport}
        withRadioButtons={withRadioButtons}
      />
    )
  }, [airportSearchPhrase, filteredAirports, handleAirportSelect, searchState.departureAirport, showNoAirportResults])

  const handleClassSelect = useCallback((fareClass: FareClassWithReward) => {
    onFlightClassChange(fareClass)
  }, [onFlightClassChange])

  const effectiveAvailableFareClasses = useMemo(() => {
    if (availableFareClasses.length > 0) {
      return availableFareClasses
    }
    return ALL_CHARTER_FARE_CLASSES
  }, [availableFareClasses])
  const allowedFareClassesForModal = useMaldivesCharterModalFareClasses(effectiveAvailableFareClasses)
  const canOpenFlightClassModal = useMemo(
    () => allowedFareClassesForModal.length > 1,
    [allowedFareClassesForModal.length],
  )
  const classEndIcon = useMemo(
    () => (canOpenFlightClassModal ? <LineAngleDownIcon /> : undefined),
    [canOpenFlightClassModal],
  )
  const modalSelectedClass = useMemo<FareClassWithReward>(() => {
    return searchState.flightClass ??
      getDefaultFareClassFromAllowed(effectiveAvailableFareClasses) ??
      effectiveAvailableFareClasses[0] ??
      FlightsClassTypes.ECONOMY
  }, [effectiveAvailableFareClasses, searchState.flightClass])

  const intl = useIntl()

  const guestsLabel = intl.formatMessage({
    id: 'YxPFtPmMUa6DaOtHR2C8',
    defaultMessage: 'Guests',
    description: 'Filter label for guest/traveller count in Maldives charter search',
  })
  const classLabel = intl.formatMessage({
    id: 'ENDfy2L5-UJw5eW1FNRY',
    defaultMessage: 'Class',
    description: 'Filter label for flight class selection in Maldives charter search',
  })
  const departureAirportLabel = intl.formatMessage({
    id: 'QRwwkKFMwzLl9le3Qh-g',
    defaultMessage: 'Departure Airport',
    description: 'Filter label for departure airport in Maldives charter search',
  })
  const whenLabel = intl.formatMessage({
    id: 'TDXR9qO_4FCCRJkinFlt',
    defaultMessage: 'When',
    description: 'Filter label for date selection in Maldives charter search',
  })
  const sectionHeading = intl.formatMessage({
    id: 'F7OeLnH_rPHwj14KyxYB',
    defaultMessage: 'Exclusive hotel + flight bundles',
    description: 'Section heading for Maldives charter hotel and flight bundle search',
  })
  const applyText = intl.formatMessage({
    id: 'zgXefx44Ms7QiVhCN97x',
    defaultMessage: 'Apply',
    description: 'Apply button text for Maldives charter search filters',
  })
  const departFromTitle = intl.formatMessage({
    id: 'YYXga68LqZycrBRl9aSM',
    defaultMessage: 'Where will you depart from?',
    description: 'Title for departure airport selection in Maldives charter search',
  })
  const selectAirportSubtitle = intl.formatMessage({
    id: '1yEoZlGqQq74EeVFX_vt',
    defaultMessage: 'Select your preferred airport',
    description: 'Subtitle for departure airport selection dropdown on the form',
  })
  const airportSearchPlaceholder = intl.formatMessage({
    id: 'JDzIcSDyx20n1j3jNEnc',
    defaultMessage: 'Search by city or airport',
    description: 'Placeholder text for airport search input in Maldives charter search',
  })

  const openFlightClassModal = async() => {
    if (!canOpenFlightClassModal) {
      return
    }
    // Scroll filter button into view when clicked
    if (isTabletScreenSizeAndUp && filtersBlockRef.current) {
      smartScrollIntoViewIfNeeded(filtersBlockRef.current)
    }
    const selectedClass = await openModal<FareClassWithReward>(<MaldivesCharterFlightFareClassModal
      selectedClassId={modalSelectedClass}
      checkInDate={searchState.checkInDate}
      checkOutDate={searchState.checkOutDate}
      occupancies={searchState.rooms}
      nights={nights}
      luxPlusEnabled={searchState.luxPlusEnabled}
      allowedFareClasses={allowedFareClassesForModal}
      rewardSeatsOnly={rewardSeatsOnly}
      onRewardSeatsOnlyChange={onRewardSeatsOnlyChange}
    />)

    if (selectedClass) {
      handleClassSelect(selectedClass)
      onCabinFilterChange?.(toBaseFareClass(selectedClass))
      if (isRewardFareClass(selectedClass)) {
        onRewardSeatsOnlyChange?.(true)
      }
    }
  }

  // Open flight calendar modal (now handled at page level)
  function handleDatesToggle() {
    // Scroll filter button into view when clicked
    if (isTabletScreenSizeAndUp && filtersBlockRef.current) {
      smartScrollIntoViewIfNeeded(filtersBlockRef.current)
    }
    onOpenFlightCalendar()
  }

  const containsChildren = searchState.rooms.some(room => (room.children ?? 0) > 0)
  const containsInfants = searchState.rooms.some(room => (room.infants ?? 0) > 0)
  const guestCount = sum(searchState.rooms, room => room.adults + (room.children ?? 0) + (room.infants ?? 0))

  const selectGuestsText = intl.formatMessage({
    id: '0UBmVhB-4fhAs6uoFQVo',
    defaultMessage: 'Select guests',
    description: 'Fallback text when no guests selected in Maldives charter search',
  })
  const guestsCountText = intl.formatMessage(
    {
      id: '9Rqx6phzvtmfA0f9DrNZ',
      defaultMessage: '{count, plural, one {# guest} other {# guests}}',
      description: 'Display text showing guest count including children for Maldives charter',
    },
    { count: guestCount },
  )
  const adultsCountText = intl.formatMessage(
    {
      id: '-LbhFX7OnQoc-cryDzCB',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Display text showing adult count for Maldives charter',
    },
    { count: guestCount },
  )
  const travellersDisplayText = searchState.rooms.length
    ? ((containsChildren || containsInfants) ? guestsCountText : adultsCountText)
    : selectGuestsText

  const selectCabinText = intl.formatMessage({
    id: 'Hy0TYfHosxFSZdBtoZ3-',
    defaultMessage: 'Select cabin',
    description: 'Fallback text when no flight class selected in Maldives charter search',
  })
  const selectedFlightClass = searchState.flightClass
  const selectedClassLabel = selectedFlightClass
    ? getCharterFareClassDisplayLabel(intl, selectedFlightClass)
    : selectCabinText

  // Calculate nights based on selected bundle
  const nights = searchState.selectedBundle === '5-nights' ? 5 : 12

  // Format selected dates for display
  // For charter bundle, display flight dates (departDate/returnDate) instead of hotel dates (checkIn/checkOut)
  // Match the style from CharterFlightCalendarSelect: use intlDateFormatter, bold the date text, and style nights as thin grey
  const selectDatesText = intl.formatMessage({
    id: '5WGKca-hS4yxKLYA-5gv',
    defaultMessage: 'Select dates',
    description: 'Fallback text when no dates selected in Maldives charter search',
  })
  const datesDisplayText = useMemo(() => {
    const nightsText = intl.formatMessage(
      {
        id: '29HUXRL6TltEI3bnet1E',
        defaultMessage: '({count, plural, one {# night} other {# nights}})',
        description: 'Nights count display in date filter for Maldives charter search',
      },
      { count: nights },
    )
    if (searchState.departDate && searchState.returnDate) {
      const dateText = `${intlDateFormatter(searchState.departDate, INTL_DAY_MONTH_NAME_SHORT, intl.locale)} - ${intlDateFormatter(searchState.returnDate, INTL_DAY_MONTH_NAME_SHORT, intl.locale)}`
      return <><b>{dateText}</b> <BodyText variant="medium" weight="normal" colour="light-foreground-alt" as="span">{nightsText}</BodyText></>
    }
    if (searchState.checkInDate && searchState.checkOutDate) {
      const dateText = `${intlDateFormatter(searchState.checkInDate, INTL_DAY_MONTH_NAME_SHORT, intl.locale)} - ${intlDateFormatter(searchState.checkOutDate, INTL_DAY_MONTH_NAME_SHORT, intl.locale)}`
      return <><b>{dateText}</b> <BodyText variant="medium" weight="normal" colour="light-foreground-alt" as="span">{nightsText}</BodyText></>
    }
    return selectDatesText
  }, [searchState.checkInDate, searchState.checkOutDate, searchState.departDate, searchState.returnDate, nights, intl, selectDatesText])

  return (
    <BundleSearchSection>
      <LayoutContainer ref={filtersGridRef}>
        <Group direction="vertical" gap={20}>
          <Heading id="maldives-direct-search-filters-heading" variant="heading2" as="h2" align="center">
            {sectionHeading}
          </Heading>

          <Group direction="vertical" gap={12}>
            <Group direction="horizontal" horizontalAlign="center">
              <CharterDurationToggleControl
                options={durationToggleOptions}
                selectedValue={nights}
                onChange={handleDurationToggleChange}
              />
            </Group>

            <ScrollAnchor ref={filtersBlockRef} vOffset={rem(12)} />
            <FiltersGrid>
              {/* Guests Filter */}
              <FilterListItem
                size="large"
                ref={inputRef}
                onClick={handleTravellersToggle}
                startIcon={<LineUserIcon />}
                endIcon={<LineAngleDownIcon />}
                overline={<BodyText variant="small" weight="normal" as="span">
                  {guestsLabel}
                </BodyText>}
                title={
                  <BodyText variant="medium" weight="bold" as="span">
                    {travellersDisplayText}
                  </BodyText>
                }
              />

              {/* Class Filter */}
              <FilterListItem
                size="large"
                onClick={openFlightClassModal}
                startIcon={<LineSeatIcon />}
                endIcon={classEndIcon}
                overline={<BodyText variant="small" weight="normal" as="span">
                  {classLabel}
                </BodyText>}
                title={
                  <BodyText variant="medium" weight="bold" as="span">
                    {selectedClassLabel}
                  </BodyText>
                }
              />

              {/* Departure Airport Filter */}
              <FilterListItem
                $mobileFullWidth
                size="large"
                ref={airportButtonRef}
                onClick={handleAirportToggle}
                startIcon={<LinePlaneIcon />}
                endIcon={<LineAngleDownIcon />}
                overline={<BodyText variant="small" weight="normal" as="span">
                  {departureAirportLabel}
                </BodyText>}
                title={
                  <FormattedMessage
                    id="Xf04GmCJpqdgJ1_e9guL"
                    defaultMessage="{airportName} ({code})"
                    description="Departure airport name with code in Maldives charter filter"
                    values={{
                      airportName: <BodyText variant="medium" weight="bold" as="span">{searchState.departureAirport.name}</BodyText>,
                      code: <BodyText variant="medium" weight="normal" as="span">{searchState.departureAirport.code}</BodyText>,
                    }}
                  />
                }
              />

              {/* When (Dates) Filter - Opens Modal at page level */}
              <FilterListItem
                $mobileFullWidth
                size="large"
                ref={datesButtonRef}
                onClick={handleDatesToggle}
                startIcon={<LineCalendarIcon />}
                endIcon={<LineAngleDownIcon />}
                overline={<BodyText variant="small" weight="normal" as="span">
                  {whenLabel}
                </BodyText>}
                title={
                  <BodyText variant="medium" weight="bold" as="span">
                    {datesDisplayText}
                  </BodyText>
                }
              />
            </FiltersGrid>
          </Group>
        </Group>
      </LayoutContainer>

      {/* Desktop Occupancy Select */}
      <MaldivesOccupancySelect
        rooms={pendingRooms}
        open={openFilter === 'travellers' && !isMobile}
        onRoomChange={handleRoomChange}
        onApply={handleRoomsApply}
        onClose={closeTravellersMenu}
        triggerRef={inputRef}
        boundaryRef={filtersGridRef as React.RefObject<HTMLElement>}
        dropdownRef={travellersDropdownRef}
      />
      {/* Mobile Occupancy Modal */}
      <Modal
        mode="drawer"
        title={guestsLabel}
        isOpen={openFilter === 'travellers' && isMobile}
        onClose={dismissTravellersMenu}
        onBack={dismissTravellersMenu}
        primaryActionText={applyText}
        onPrimaryActionClick={handleRoomsApply}
        headerExtension={
          <Group direction="horizontal" horizontalAlign="end">
            <BodyText variant="large">
              <FormattedOccupancy
                occupancies={pendingRooms}
                saleUnit="room"
              />
            </BodyText>
          </Group>
        }
      >
        <div ref={travellersMobileRef}>
          <OccupancyPicker
            saleUnit="room"
            occupancies={pendingRooms}
            onChange={handleRoomChange}
            enableMultiRoom={config.accommodation.allowsMultiRoom}
            childVariant="child-age"
            maxChildAge={maxFlightChildAge}
          />
        </div>
      </Modal>

      {/* Desktop Airport Selection Dropdown */}
      <DropdownList
        ref={airportDropdownRef}
        size="M"
        open={openFilter === 'airport' && !isMobile}
        anchorRef={airportButtonRef}
        triggerRef={airportButtonRef}
        title={departFromTitle}
        subtitle={selectAirportSubtitle}
        onClose={closeAirportDropdown}
        primaryActionProps={{
          children: applyText,
          onClick: closeAirportDropdown,
        }}
        headerExtension={<TextInputOnly
          placeholder={airportSearchPlaceholder}
          noValidationMessage
          noValidationSpacing
          value={airportSearchPhrase}
          onChange={handleAirportSearchChange}
          endIcon={<LineSearchIcon />}
        />}
      >
        {getAirportOptionsList()}
      </DropdownList>
      <Backdrop visible={!!(openFilter === 'airport' && !isMobile)} onClick={closeAirportDropdown} data-testid="airport-select-backdrop" />

      {/* Mobile Airport Selection Modal */}
      <Modal
        mode="drawer"
        title={departFromTitle}
        isOpen={openFilter === 'airport' && isMobile}
        onClose={closeAirportDropdown}
        onBack={closeAirportDropdown}
        flushListItems
      >
        <VerticalSpacer gap={12}>
          <TextInputOnly
            placeholder={airportSearchPlaceholder}
            noValidationMessage
            noValidationSpacing
            value={airportSearchPhrase}
            onChange={handleAirportSearchChange}
            endIcon={<LineSearchIcon />}
          />
          {getAirportOptionsList()}
        </VerticalSpacer>
      </Modal>
    </BundleSearchSection>
  )
}

export default MaldivesDirectSearchFilters
